import { useState } from "react";
import { knowledgeApi } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/SimpleToast";
import { Loader2, Plus, Trash2, FolderCog, Lock } from "lucide-react";

const DEFAULT_CATEGORIES = ["Anleitungen", "FAQ", "Produkte", "Technisch", "Intern", "Sonstiges"];

export const CategoryManager = ({ open, onOpenChange, categories, onChanged }) => {
  const toast = useToast();
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await knowledgeApi.createCategory(name);
      toast.success("Kategorie erstellt");
      setNewName("");
      if (onChanged) onChanged(res.data.categories);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (name) => {
    setDeleting(name);
    try {
      const res = await knowledgeApi.deleteCategory(name);
      toast.success("Kategorie gelöscht");
      if (onChanged) onChanged(res.data.categories);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="category-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderCog className="w-5 h-5" />
            Kategorien verwalten
          </DialogTitle>
          <DialogDescription>
            Eigene Kategorien hinzufügen oder entfernen. Standard-Kategorien sind geschützt.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleAdd} className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Neue Kategorie..."
            data-testid="new-category-input"
          />
          <Button type="submit" disabled={saving || !newName.trim()} data-testid="add-category-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </form>

        <div className="space-y-2 max-h-[320px] overflow-y-auto mt-2">
          {categories.map((cat) => {
            const isDefault = DEFAULT_CATEGORIES.includes(cat);
            return (
              <div
                key={cat}
                className="flex items-center justify-between rounded-md border px-3 py-2"
                data-testid={`category-row-${cat}`}
              >
                <span className="flex items-center gap-2 text-sm">
                  {cat}
                  {isDefault && <Badge variant="outline" className="text-[10px] gap-1"><Lock className="w-3 h-3" />Standard</Badge>}
                </span>
                {!isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(cat)}
                    disabled={deleting === cat}
                    data-testid={`delete-category-${cat}`}
                  >
                    {deleting === cat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
